import { ENCRYPTION_EXPIRY_DURATION } from '../constants';
import { EncryptedObj, EncryptionData, ErrorData, ErrorTypes, FieldsData, ValidFieldTypes } from '../types';
import { PayRequestDataFromFields } from './config';
import { getExpDateForEncryptedData } from './date';

export async function encryptData(data: unknown, public_key: string): Promise<string> {
  const encoder = new TextEncoder();
  const encodedData = encoder.encode(JSON.stringify(data));

  // Delete PEM-headers
  const pemHeader = '-----BEGIN PUBLIC KEY-----';
  const pemFooter = '-----END PUBLIC KEY-----';
  const pemContents = public_key.replace(pemHeader, '').replace(pemFooter, '').replace(/\s/g, '');

  const publicKey = await window.crypto.subtle.importKey(
    'spki',
    Uint8Array.from(atob(pemContents), c => c.charCodeAt(0)), 
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt']
  );

  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    encodedData
  );

  return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
}

function enrichEncryptionWithBIN(
  encryptedObj: EncryptedObj,
  fieldsData: Partial<FieldsData>,
  includeBIN: boolean,
): EncryptedObj {
  const cardField = fieldsData[ValidFieldTypes.CARD_NUMBER];

  if (includeBIN && cardField && !cardField.isError) {
    return {
      ...encryptedObj,
      BIN: cardField.data.toString().slice(0, 8),
    };
  }

  return encryptedObj;
}

function enrichEncryptionWithACHInfo(
  encryptedObj: EncryptedObj,
  fieldsData: Partial<FieldsData>,
  includeACHInfo: boolean,
): EncryptedObj {
  const routingField = fieldsData[ValidFieldTypes.ROUTING];
  const accountField = fieldsData[ValidFieldTypes.ACCOUNT_NUMBER];

  if (includeACHInfo &&
    accountField && !accountField.isError &&
    routingField && !routingField.isError
  ) {
    return {
      ...encryptedObj,
      RoutingNumber: routingField.data.toString(),
      AccountNumberLastFour: accountField.data.toString().slice(-4),
    };
  }

  return encryptedObj;
}

export async function encryptFieldsData(
  fieldsData: Partial<FieldsData>,
  encryptionData: EncryptionData,
  includeBIN: boolean,
  includeACHInfo: boolean,
): Promise<{
  isSuccess: boolean;
  errors?: ErrorData[];
  Encrypted?: EncryptedObj;
}> {
  try {
    const preparedData = Object.entries(fieldsData).reduce((acc, [fieldName, fieldData]) => {
      acc[PayRequestDataFromFields[fieldName]] = fieldData.data;
      return acc;
    }, { Expires: getExpDateForEncryptedData(ENCRYPTION_EXPIRY_DURATION)});

    const encryptedFieldsData = await encryptData(preparedData, encryptionData.key);

    let encryptedObj: EncryptedObj = {
      Data: encryptedFieldsData,
      KeyId: encryptionData.keyId,
    };

    encryptedObj = enrichEncryptionWithBIN(encryptedObj, fieldsData, includeBIN);
    encryptedObj = enrichEncryptionWithACHInfo(encryptedObj, fieldsData, includeACHInfo);

    return {
      isSuccess: true,
      Encrypted: encryptedObj,
    };
  } catch (error) {
    const errResult = {
      isSuccess: false,
      errors: [{
        errorType: ErrorTypes.ENCRYPT_ERROR,
        errorText: '',
      }],
    };

    if (error instanceof DOMException) {
      errResult.errors[0].errorText = `Crypto API error: ${error?.message}`;
    } else if (error instanceof TypeError) {
      errResult.errors[0].errorText = `Invalid input data: ${error?.message}`;
    } else {
      errResult.errors[0].errorText = `Unexpected error: ${(error as Error)?.message}`;
    }

    return errResult;
  }
}
